import {
  authControllerChangeMyPassword,
  type ChangePasswordDtoReq,
} from "@/shared/api/generated"
import { useMutation } from "@tanstack/react-query"
import { AxiosError } from "axios"
import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { toast } from "sonner"
import { useAutoTranslation } from "@/entities"
import { DISCOVERED_TRANSLATION_KEYS, LANG_KEYS } from "@/shared/constants"

interface FormData {
  oldPassword: string
  newPassword: string
  confirmNewPassword: string
}

export function useUserChangePassForm() {
  const { t } = useAutoTranslation()
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    defaultValues: {
      confirmNewPassword: "",
      newPassword: "",
      oldPassword: "",
    },
  })

  const [isLoading, setIsLoading] = useState(false)
  const [formError, setFormError] = useState("")
  const [passConfErr, setPassErr] = useState<string>("")

  const formMutation = useMutation({
    mutationFn: (data: ChangePasswordDtoReq) =>
      authControllerChangeMyPassword(data),
    async onSuccess() {
      reset()
      setIsLoading(false)
      setFormError("")
      toast.success(
        t(DISCOVERED_TRANSLATION_KEYS.PASSWORD_CHANGED_SUCCESSFULLY),
        {
          duration: 1000 * 60 * 1, // 1 minutes,
          dismissible: true,
          closeButton: true,
        }
      )
    },
    onError: e => {
      setIsLoading(false)
      if (e instanceof AxiosError) {
        setFormError(
          e.response?.data.message ?? t(LANG_KEYS.SOMETHING_WENT_WRONG)
        )
      }
    },
  })

  const passConf = watch("confirmNewPassword")

  useEffect(() => {
    if (passConf && passConfErr) {
      setPassErr("")
    }
  }, [passConf])

  return {
    errors,
    control,
    passConfErr,
    isLoading,
    formError,
    setFormError,
    handleSubmit: handleSubmit(values => {
      if (values.newPassword !== values.confirmNewPassword) {
        setPassErr(t(LANG_KEYS.PASSWORDS_DONT_MATCH))
        return
      }
      setIsLoading(true)
      setFormError("")

      const payload: ChangePasswordDtoReq = {
        oldPass: values.oldPassword,
        newPass: values.newPassword,
      }
      formMutation.mutate(payload)
    }),
  }
}
